import React, { useEffect, useState } from "react";
import {
    THEME_PRESETS,
    getThemeVars,
    isCustomTheme,
    parseCustomTheme,
    buildCustomTheme,
    contrastWithWhite,
} from "../utils/businessThemes";

const HEX_RE = /^#[0-9a-f]{6}$/i;

/* a colour <input> paired with a hex text box; the text box accepts typing/pasting a code */
function ColourField({ label, hint, value, onChange }) {
    const [text, setText] = useState(value);
    useEffect(() => { setText(value); }, [value]);

    const commit = (v) => {
        const t = v.startsWith("#") ? v : `#${v}`;
        setText(v);
        if (HEX_RE.test(t)) onChange(t.toLowerCase());
    };

    return (
        <div className="col-md-6">
            <label className="form-label small fw-semibold mb-0">{label}</label>
            <div className="text-muted small mb-1">{hint}</div>
            <div className="d-flex align-items-center gap-2">
                <input
                    type="color"
                    className="form-control form-control-color"
                    value={value}
                    onChange={(e) => onChange(e.target.value.toLowerCase())}
                    title={label}
                    aria-label={label}
                />
                <input
                    className="form-control form-control-sm"
                    style={{ maxWidth: 120, fontFamily: "monospace" }}
                    value={text}
                    maxLength={7}
                    spellCheck={false}
                    onChange={(e) => commit(e.target.value.trim())}
                    onBlur={() => setText(value)}
                    aria-label={`${label} hex code`}
                />
            </div>
        </div>
    );
}

export default function PoliticalThemeTab({ form, set }) {
    const current = form.theme_color || "classic-navy";
    const custom = parseCustomTheme(current);
    const isCustom = isCustomTheme(current);
    const vars = getThemeVars(current);

    // Picking "Custom" starts from whatever theme is active, so nothing jumps.
    const startCustom = () => {
        if (isCustom) return;
        set("theme_color", buildCustomTheme(vars.brand, vars.accent));
    };
    const setCustom = (patch) => {
        const base = custom || { brand: vars.brand, accent: vars.accent };
        const next = { ...base, ...patch };
        set("theme_color", buildCustomTheme(next.brand, next.accent));
    };

    const lowContrast = isCustom && contrastWithWhite(vars.brand) < 4.5;

    return (
        <div className="card shadow-sm mb-3">
            <div className="card-header bg-light fw-semibold">Website Theme Colour</div>
            <div className="card-body">
                <p className="text-muted small mb-2">Pick a ready-made theme, or choose your own colours.</p>

                <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
                    {THEME_PRESETS.map((t) => {
                        const on = !isCustom && current === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                title={t.label}
                                aria-label={t.label}
                                aria-pressed={on}
                                onClick={() => set("theme_color", t.key)}
                                style={{
                                    width: 40, height: 40, borderRadius: 8, border: "none",
                                    background: `linear-gradient(135deg, ${t.brand}, ${t.brand2})`,
                                    outline: on ? `3px solid ${t.accent}` : "2px solid transparent",
                                    outlineOffset: 2, cursor: "pointer", color: "#fff", transition: "all 0.2s",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}
                            >
                                {on && <i className="bi bi-check-lg" />}
                            </button>
                        );
                    })}

                    <button
                        type="button"
                        title="Custom colours"
                        aria-label="Custom colours"
                        aria-pressed={isCustom}
                        onClick={startCustom}
                        style={{
                            height: 40, padding: "0 14px", borderRadius: 8, border: "1px dashed #8a94a3",
                            background: isCustom ? `linear-gradient(135deg, ${vars.brand}, ${vars.accent})` : "#fff",
                            color: isCustom ? "#fff" : "#334", fontWeight: 600, fontSize: 13, cursor: "pointer",
                            outline: isCustom ? `3px solid ${vars.accent}` : "2px solid transparent", outlineOffset: 2,
                            display: "flex", alignItems: "center", gap: 6,
                        }}
                    >
                        <i className={`bi ${isCustom ? "bi-check-lg" : "bi-palette-fill"}`} />
                        Custom
                    </button>
                </div>

                {isCustom && (
                    <div className="border rounded p-3 mb-3 bg-light">
                        <div className="row g-3">
                            <ColourField
                                label="Main colour"
                                hint="Header, footer, headings and the hero background."
                                value={custom.brand}
                                onChange={(v) => setCustom({ brand: v })}
                            />
                            <ColourField
                                label="Accent colour"
                                hint="Highlights, party ribbon, active menu underline."
                                value={custom.accent}
                                onChange={(v) => setCustom({ accent: v })}
                            />
                        </div>
                        {lowContrast && (
                            <div className="alert alert-warning py-2 small mt-3 mb-0">
                                <i className="bi bi-exclamation-triangle me-1" />
                                The main colour is quite light — the white text on your header and footer may be hard to read. A darker shade will look better.
                            </div>
                        )}
                        <button type="button" className="btn btn-sm btn-link px-0 mt-2" onClick={() => set("theme_color", "classic-navy")}>
                            Reset to Classic Navy
                        </button>
                    </div>
                )}

                {/* live preview of how the chosen colours are used on the site */}
                <div className="small fw-semibold mb-1">Preview</div>
                <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #dee2e6", maxWidth: 520 }}>
                    <div style={{ background: vars.brand, color: "#fff", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: 14 }}>
                        <span>Your Name</span>
                        <span style={{ fontSize: 12, fontWeight: 600, borderBottom: `2px solid ${vars.accent}`, paddingBottom: 2 }}>About</span>
                    </div>
                    <div style={{ background: `linear-gradient(135deg, ${vars.brand}, ${vars.brand2})`, color: "#fff", padding: "18px 14px" }}>
                        <div style={{ fontWeight: 800, fontSize: 18 }}>Serving the people</div>
                        <span style={{ display: "inline-block", marginTop: 8, background: `linear-gradient(135deg, ${vars.accent}, ${vars.accentD})`, padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
                            Party ribbon
                        </span>
                    </div>
                </div>

                <div className="form-text mt-2">
                    <b>Selected:</b> {isCustom ? `Custom (${vars.brand} / ${vars.accent})` : (THEME_PRESETS.find((t) => t.key === current)?.label || "Classic Navy")} — this changes the colours on your live political website.
                </div>
            </div>
        </div>
    );
}